import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import Modal from 'react-modal';
import './StudentAdmin.css';
import { Link } from 'react-router-dom';

// Import Firestore functions and db instance
import { db } from '../Firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";

Modal.setAppElement('#root');

// Helper functions (remain the same)
const formatDobInput = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, '').substring(0, 8);
  if (digits.length > 4) { return `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4)}`; }
  else if (digits.length > 2) { return `${digits.substring(0, 2)}-${digits.substring(2)}`;}
  return digits;
};
const validateDob = (dobString) => {
  if (!dobString) return null;
  if (!/^\d{2}-\d{2}-\d{4}$/.test(dobString)) { return 'รูปแบบวันเกิดไม่ถูกต้อง กรุณาใช้ DD-MM-YYYY';}
  const [day, month, year] = dobString.split('-').map(Number);
  if (year < 2500) { return 'ปีต้องเป็นปี พ.ศ. และไม่น้อยกว่า 2500';}
  if (month < 1 || month > 12) { return 'เดือนต้องอยู่ระหว่าง 01 ถึง 12';}
  if (day < 1 || day > 31) { return 'วันที่ต้องอยู่ระหว่าง 01 ถึง 31';}
  const date = new Date(year - 543, month - 1, day);
  if (date.getDate() !== day || (date.getMonth() + 1) !== month) { return 'วันที่ไม่ถูกต้อง (เช่น เดือนกุมภาพันธ์ มีไม่ถึง 31 วัน)';}
  return null;
};
const formatPhoneNumber = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, '');
  const truncatedDigits = digits.substring(0, 10);
  if (truncatedDigits.length > 3) { return `${truncatedDigits.substring(0, 3)}-${truncatedDigits.substring(3)}`;}
  return truncatedDigits;
};

// Helper to convert Google Drive view links to direct image links
const formatGoogleDriveImageUrl = (url) => {
  if (!url) return null;
  // Check for common Google Drive ID patterns
  const idMatch = url.match(/[-\w]{25,}/);
  if (url.includes('drive.google.com') && idMatch) {
    // ใช้ lh3.googleusercontent.com/d/ ซึ่งมักจะแสดงผลรูปภาพได้ดีกว่าแบบ /uc
    return `https://lh3.googleusercontent.com/d/${idMatch[0]}`;
  }
  return url;
};

// 1. กำหนดลำดับและชื่อที่แสดงของระดับชั้น
const gradeLevels = [
    'อนุบาล1', 'อนุบาล2', 'อนุบาล3', 'ประถมปีที่1', 'ประถมปีที่2', 'ประถมปีที่3',
    'ประถมปีที่4', 'ประถมปีที่5', 'ประถมปีที่6', 'มัธยม1', 'มัธยม2', 'มัธยม3',
    'มัธยม4', 'มัธยม5', 'มัธยม6', 'อื่นๆ'
];
const gradeDisplayMap = {
    'อนุบาล1': 'อนุบาล 1', 'อนุบาล2': 'อนุบาล 2', 'อนุบาล3': 'อนุบาล 3',
    'ประถมปีที่1': 'ประถมปีที่ 1', 'ประถมปีที่2': 'ประถมปีที่ 2', 'ประถมปีที่3': 'ประถมปีที่ 3',
    'ประถมปีที่4': 'ประถมปีที่ 4', 'ประถมปีที่5': 'ประถมปีที่ 5', 'ประถมปีที่6': 'ประถมปีที่ 6',
    'มัธยม1': 'มัธยม 1', 'มัธยม2': 'มัธยม 2', 'มัธยม3': 'มัธยม 3',
    'มัธยม4': 'มัธยม 4', 'มัธยม5': 'มัธยม 5', 'มัธยม6': 'มัธยม 6',
    'อื่นๆ': 'อื่นๆ'
};

// 2.ฟังก์ชันสำหรับคำนวณระดับชั้นปัจจุบัน
const calculateCurrentGrade = (student) => {
  const initialGrade = student['school-level'];
  const studentId = String(student.ID || '');

  if (!initialGrade || !studentId || studentId.length < 2) {
      return gradeDisplayMap[initialGrade] || initialGrade || 'N/A';
  }

  // --- START: แก้ไขการคำนวณปี ---
  // คำนวณปี พ.ศ. ปัจจุบันจากวันที่ปัจจุบัน และเอาเลข 2 ตัวท้ายมาใช้
  const currentBuddhistYear = new Date().getFullYear() + 543;
  const currentYearShort = currentBuddhistYear % 100;
  // --- END: แก้ไขการคำนวณปี ---
  
  const idYear = parseInt(studentId.substring(0, 2), 10);

  if (isNaN(idYear)) {
      return gradeDisplayMap[initialGrade] || initialGrade || 'N/A';
  }
  const adjustmentValue = currentYearShort - idYear;

  const initialIndex = gradeLevels.indexOf(initialGrade);
  if (initialIndex === -1) {
      return gradeDisplayMap[initialGrade] || initialGrade || 'N/A';
  }
  const newIndex = initialIndex + adjustmentValue;

  let finalGradeKey;
  if (newIndex >= gradeLevels.length - 1) {
      finalGradeKey = 'อื่นๆ';
  } else if (newIndex < 0) {
      finalGradeKey = gradeLevels[0];
  } else {
      finalGradeKey = gradeLevels[newIndex];
  }
  return gradeDisplayMap[finalGradeKey] || finalGradeKey;
};

function StudentAdmin() {
  const [allStudents, setAllStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'ID', direction: 'descending' });

  // Fetch data from Firestore
  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(db, "student-register"));
      const studentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllStudents(studentsData);
    } catch (err) {
      console.error("Error fetching documents: ", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedAndFilteredStudents = useMemo(() => {
    let filteredItems = [...allStudents];
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      filteredItems = allStudents.filter(student =>
        student.name?.toLowerCase().includes(lowercasedTerm) ||
        student.surname?.toLowerCase().includes(lowercasedTerm) ||
        student.nickname?.toLowerCase().includes(lowercasedTerm)
      );
    }
    if (sortConfig.key !== null) {
      filteredItems.sort((a, b) => {
        let valA, valB;

        if (sortConfig.key === 'currentGrade') {
          const gradeAString = calculateCurrentGrade(a);
          const gradeBString = calculateCurrentGrade(b);
          const keyA = Object.keys(gradeDisplayMap).find(key => gradeDisplayMap[key] === gradeAString);
          const keyB = Object.keys(gradeDisplayMap).find(key => gradeDisplayMap[key] === gradeBString);
          valA = gradeLevels.indexOf(keyA);
          valB = gradeLevels.indexOf(keyB);
        } else {
          valA = sortConfig.key === 'ID' ? Number(a[sortConfig.key]) || 0 : a[sortConfig.key];
          valB = sortConfig.key === 'ID' ? Number(b[sortConfig.key]) || 0 : b[sortConfig.key];
        }

        if (valA < valB) { return sortConfig.direction === 'ascending' ? -1 : 1; }
        if (valA > valB) { return sortConfig.direction === 'ascending' ? 1 : -1; }
        return 0;
      });
    }
    return filteredItems;
  }, [allStudents, searchTerm, sortConfig]);

  const openEditModal = (student) => {
    let dobForInput = '';
    if (student.birthday) {
      // Check if it's already in DD-MM-YYYY or DD/MM/YYYY format
      if (/^\d{2}-\d{2}-\d{4}$/.test(student.birthday)) {
        dobForInput = student.birthday;
      } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(student.birthday)) {
        dobForInput = student.birthday.replace(/\//g, '-');
      } else {
        // Try parsing as standard date
        const date = new Date(student.birthday);
        if (!isNaN(date.getTime())) {
             dobForInput = date.toLocaleDateString('en-GB').replace(/\//g, '-');
        } else {
            // Fallback: use original string if parsing fails
            dobForInput = student.birthday;
        }
      }
    }
    setEditingStudent({ ...student, birthday: dobForInput });
    setIsModalOpen(true);
  };

  const closeEditModal = () => { setIsModalOpen(false); setEditingStudent(null); };

  const handleModalFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telephone1' || name === 'telephone2') { setEditingStudent(prev => ({ ...prev, [name]: formatPhoneNumber(value) })); }
    else if (name === 'birthday') { setEditingStudent(prev => ({ ...prev, [name]: formatDobInput(value) }));}
    else { setEditingStudent(prev => ({ ...prev, [name]: value }));}
  };

  const handleDelete = async (student) => {
    Swal.fire({
      title: `ยืนยันการลบ`, text: `คุณต้องการลบข้อมูลของ ${student.name} ${student.surname} ใช่หรือไม่?`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6', confirmButtonText: 'ใช่, ลบเลย!', cancelButtonText: 'ยกเลิก'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({ title: 'กำลังลบ...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        try {
          await deleteDoc(doc(db, "student-register", student.id));
          Swal.fire('ลบสำเร็จ!', 'ข้อมูลถูกลบเรียบร้อย', 'success');
          fetchData(); // Refresh data
        } catch (err) {
          console.error("Error deleting document: ", err);
          Swal.fire('เกิดข้อผิดพลาด', err.message, 'error');
        }
      }
    });
  };

  const handleModalFormSubmit = async (e) => {
    e.preventDefault();
    const dobError = validateDob(editingStudent.birthday);
    if (dobError) {
      Swal.fire('ข้อมูลไม่ถูกต้อง', dobError, 'error');
      return;
    }
    Swal.fire({ title: 'กำลังบันทึก...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    
    const updatedData = {
      sex: editingStudent.sex || "",
      name: editingStudent.name || "",
      surname: editingStudent.surname || "",
      nickname: editingStudent.nickname || "",
      birthday: editingStudent.birthday || "",
      religion: editingStudent.religion || "",
      address: editingStudent.address || "",
      school: editingStudent.school || "",
      'school-level': editingStudent['school-level'] || "",
      'father-name': editingStudent['father-name'] || "",
      'father-age': editingStudent['father-age'] || "",
      'father- occupation': editingStudent['father- occupation'] || "",
      'mother-name': editingStudent['mother-name'] || "",
      'mother-age': editingStudent['mother-age'] || "",
      'mother- occupation': editingStudent['mother- occupation'] || "",
      telephone1: editingStudent.telephone1 || "",
      telephone2: editingStudent.telephone2 || "",
      channel: editingStudent.channel || "",
      'book-delivery': editingStudent['book-delivery'] || "",
      'child-number': Number(editingStudent['child-number']) || 0,
      'child1-age': Number(editingStudent['child1-age']) || 0,
      'child2-age': Number(editingStudent['child2-age']) || 0,
    };

    try {
      const studentRef = doc(db, "student-register", editingStudent.id);
      await updateDoc(studentRef, updatedData);
      
      closeEditModal();
      Swal.fire('บันทึกสำเร็จ!', 'ข้อมูลถูกแก้ไขเรียบร้อย', 'success');
      fetchData(); // Refresh data
    } catch (err) {
      console.error("Error updating document: ", err);
      Swal.fire('เกิดข้อผิดพลาด', err.message, 'error');
    }
  };


return (
  <div className="admin-container">
    <header className="admin-header">
      <Link to="/main" className="header-brand">
        <img src="/Wisdom-Logo.png" alt="Logo" className="header-logo" />
      </Link>
      <h1>ระบบจัดการข้อมูลนักเรียน</h1>
      <div className="search-bar">
        <div className="search-input-container">
          <input
            type="text"
            placeholder="ค้นหาจากชื่อ, นามสกุล, หรือชื่อเล่น..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>
    </header>

      {error && <div className="error-message">เกิดข้อผิดพลาด: {error}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>รูป</th>
              <th className="sortable" onClick={() => requestSort('ID')}>ID</th>
              <th className="sortable" onClick={() => requestSort('name')}>ชื่อ - นามสกุล</th>
              <th className="sortable" onClick={() => requestSort('nickname')}>ชื่อเล่น</th>
              <th>เบอร์ติดต่อ</th>
              <th className="sortable" onClick={() => requestSort('school')}>โรงเรียน</th>
              <th className="sortable" onClick={() => requestSort('currentGrade')}>ระดับชั้นปัจจุบัน</th>
              <th>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
                <tr><td colSpan="8" className="loading-cell">กำลังดึงข้อมูล...</td></tr>
            ) : sortedAndFilteredStudents.length > 0 ? (
                sortedAndFilteredStudents.map(student => (
                  <tr key={student.id}>
                    <td data-label="รูป"><img src={formatGoogleDriveImageUrl(student['image-link']) || 'https://placehold.co/50x50'} alt={student.name} className="student-avatar" referrerPolicy="no-referrer" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/50x50'; }}/></td>
                    <td data-label="ID">{student.ID} </td>
                    <td data-label="ชื่อ-สกุล"><span className="student-first-name">{student.name}</span>{student.surname}</td>
                    <td data-label="ชื่อเล่น">{student.nickname}</td>
                    <td data-label="เบอร์ติดต่อ">{student.telephone1} {student.telephone2 && `, ${student.telephone2}`}</td>
                    <td data-label="โรงเรียน">{student.school}</td>
                    <td data-label="ระดับชั้นปัจจุบัน">{calculateCurrentGrade(student)}</td>
                    <td data-label="จัดการ">
                      <button className="edit-btn" onClick={() => openEditModal(student)}>✏️ แก้ไข</button>
                      <button className="delete-btn" onClick={() => handleDelete(student)}>🗑️ ลบ</button>
                    </td>
                  </tr>
                ))
            ) : (
                <tr><td colSpan="8" className="no-data-cell">ไม่พบข้อมูลนักเรียน</td></tr>
            )}
          </tbody>
        </table>
      </div>

    <Modal isOpen={isModalOpen} onRequestClose={closeEditModal} contentLabel="แก้ไขข้อมูลนักเรียน" className="modal-content" overlayClassName="modal-overlay">
      {editingStudent && (
        <form onSubmit={handleModalFormSubmit} className="modal-form">
          {/* ── Sticky Header ── */}
          <div className="modal-header-container">
            <div className="modal-brand">
              <img src="/Wisdom-Logo.png" alt="Wisdom Logo" className="modal-logo" />
              <div className="modal-title-block">
                <span className="modal-title-label">แก้ไขข้อมูลนักเรียน</span>
                <span className="modal-student-name">{editingStudent.sex} {editingStudent.name} {editingStudent.surname}</span>
              </div>
            </div>
            <div className="modal-header-right">
              <img
                src={formatGoogleDriveImageUrl(editingStudent['image-link']) || 'https://placehold.co/80x80'}
                alt="รูปนักเรียน"
                className="modal-image-preview"
                referrerPolicy="no-referrer"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/80x80'; }}
              />
              <button type="button" className="modal-close-btn" onClick={closeEditModal} aria-label="ปิด">✕</button>
            </div>
          </div>

          {/* ── Scrollable Body ── */}
          <div className="modal-body">
            <div className="form-sections">
              <div className="form-section">
                <h3>👤 ข้อมูลส่วนตัว</h3>
                <div className="form-grid">
                  <div className="form-group"><label>เพศ</label><select name="sex" value={editingStudent.sex || ''} onChange={handleModalFormChange}><option value="ด.ช.">ด.ช.</option><option value="ด.ญ.">ด.ญ.</option><option value="นาย">นาย</option><option value="นางสาว">นางสาว</option></select></div>
                  <div className="form-group"><label>ชื่อ</label><input type="text" name="name" value={editingStudent.name || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>นามสกุล</label><input type="text" name="surname" value={editingStudent.surname || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>ชื่อเล่น</label><input type="text" name="nickname" value={editingStudent.nickname || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>วันเกิด (DD-MM-YYYY)</label><input type="text" name="birthday" value={editingStudent.birthday || ''} onChange={handleModalFormChange} placeholder="DD-MM-YYYY" maxLength="10" /></div>
                  <div className="form-group"><label>ศาสนา</label><select name="religion" value={editingStudent.religion || ''} onChange={handleModalFormChange}><option value="พุทธ">พุทธ</option><option value="อิสลาม">อิสลาม</option><option value="คริสต์">คริสต์</option><option value="อื่นๆ">อื่นๆ</option></select></div>
                  <div className="form-group full-width"><label>ที่อยู่</label><textarea name="address" value={editingStudent.address || ''} onChange={handleModalFormChange}></textarea></div>
                </div>
              </div>

              <div className="form-section">
                <h3>🎓 ข้อมูลการศึกษา</h3>
                <div className="form-grid">
                  <div className="form-group"><label>โรงเรียน</label><input type="text" name="school" value={editingStudent.school || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>ระดับชั้น (ตอนสมัคร)</label><select name="school-level" value={editingStudent['school-level'] || ''} onChange={handleModalFormChange}><option value="อนุบาล1">อนุบาล 1</option><option value="อนุบาล2">อนุบาล 2</option><option value="อนุบาล3">อนุบาล 3</option><option value="ประถมปีที่1">ประถมปีที่ 1</option><option value="ประถมปีที่2">ประถมปีที่ 2</option><option value="ประถมปีที่3">ประถมปีที่ 3</option><option value="ประถมปีที่4">ประถมปีที่ 4</option><option value="ประถมปีที่5">ประถมปีที่ 5</option><option value="ประถมปีที่6">ประถมปีที่ 6</option><option value="มัธยม1">มัธยม 1</option><option value="มัธยม2">มัธยม 2</option><option value="มัธยม3">มัธยม 3</option><option value="มัธยม4">มัธยม 4</option><option value="มัธยม5">มัธยม 5</option><option value="มัธยม6">มัธยม 6</option><option value="อื่นๆ">อื่นๆ</option></select></div>
                </div>
              </div>

              <div className="form-section">
                <h3>👪 ข้อมูลผู้ปกครองและพี่น้อง</h3>
                <div className="form-grid">
                  <div className="form-group"><label>ชื่อ-สกุลบิดา</label><input type="text" name="father-name" value={editingStudent['father-name'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>อายุบิดา</label><input type="number" name="father-age" value={editingStudent['father-age'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>อาชีพบิดา</label><input type="text" name="father- occupation" value={editingStudent['father- occupation'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>ชื่อ-สกุลมารดา</label><input type="text" name="mother-name" value={editingStudent['mother-name'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>อายุมารดา</label><input type="number" name="mother-age" value={editingStudent['mother-age'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>อาชีพมารดา</label><input type="text" name="mother- occupation" value={editingStudent['mother- occupation'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>จำนวนพี่น้อง</label><input type="number" name="child-number" value={editingStudent['child-number'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>คนที่ 1 อายุ</label><input type="number" name="child1-age" value={editingStudent['child1-age'] || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group"><label>คนที่ 2 อายุ</label><input type="number" name="child2-age" value={editingStudent['child2-age'] || ''} onChange={handleModalFormChange} /></div>
                </div>
              </div>

              <div className="form-section">
                <h3>📞 ข้อมูลการติดต่อ</h3>
                <div className="form-grid">
                  <div className="form-group"><label>เบอร์ติดต่อ 1</label><input type="tel" name="telephone1" value={editingStudent.telephone1 || ''} onChange={handleModalFormChange} placeholder="xxx-xxxxxxx" maxLength="11" /></div>
                  <div className="form-group"><label>เบอร์ติดต่อ 2</label><input type="tel" name="telephone2" value={editingStudent.telephone2 || ''} onChange={handleModalFormChange} placeholder="xxx-xxxxxxx" maxLength="11" /></div>
                  <div className="form-group full-width"><label>ช่องทางมาเป็นลูกค้า</label><input type="text" name="channel" value={editingStudent.channel || ''} onChange={handleModalFormChange} /></div>
                  <div className="form-group full-width"><label>ที่อยู่จัดส่งหนังสือ</label><textarea name="book-delivery" value={editingStudent['book-delivery'] || ''} onChange={handleModalFormChange}></textarea></div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Sticky Footer ── */}
          <div className="modal-actions">
            <button type="button" onClick={closeEditModal} className="cancel-btn">✕ ยกเลิก</button>
            <button type="submit" className="save-btn">💾 บันทึกการเปลี่ยนแปลง</button>
          </div>
        </form>
      )}
    </Modal>
  </div>
);
}

export default StudentAdmin;